import type { ReactNode } from 'react';
import {
  Card,
  CardOrientation,
  CardVariations,
} from 'components/Toolkit/Card/Card';
import { useUserContext } from 'contexts/UserContext';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { IFinancePricePerMonthSearchModel } from 'api/types/accountsApiTypes';
import { formatAuthorizationHeader } from 'helpers/auth';
import { TrustIndicatorTags } from 'components/PageWrappers/SharedComponents/TrustIndicatorTags/TrustIndicatorTags';
import type {
  PhotoView,
  DealerSummary,
  IOldPrice,
  ICardPayment,
  IMerits,
} from 'types';
import type { ThemeTypes } from 'types/Theme.namespace';
import { useSaveAd } from 'components/Support/UseSaveAd/useSaveAd';

export interface CadCardProps {
  adId?: number;
  variant: CardVariations;
  orientation: CardOrientation;
  header: string;
  price?: string;
  oldPriceView?: IOldPrice;
  currency?: 'EUR' | 'GBP';
  keyInfo?: string[];
  age?: string;
  county?: string;
  countyTown?: string;
  sectionDisplayName?: string;
  sectionName?: string;
  sellerType?: 'TRADER' | 'PRIVATE' | 'DEALER';
  meritsV2?: IMerits[];
  financeSummary?: IFinancePricePerMonthSearchModel;
  photos?: PhotoView[];
  userSaved?: boolean;
  mediaCount?: number;
  dealer?: DealerSummary;
  manufacturerApprovedBadge?: { badgeUrl?: string; manufacturer?: string };
  wanted?: boolean;
  herdwatchVerified?: boolean;
  specialOffer?: string;
  greenlightVerified?: boolean;
  cardPayments?: ICardPayment[];
  deliveryAvailable?: boolean;
  imageAlt?: string;
  cdnUrl: string;
  id: number;
  /** Manually hide save option */
  hideSave?: boolean;
  highlight?: ThemeTypes.Colors;
  highlightText?: string;
  isGrid?: boolean;
  isMobile?: boolean;
  priceOnRequest?: boolean;
  renderAdditionalHeaderInfo?: ReactNode;
  renderSubtitle?: ReactNode;
  shouldTruncateHeader?: boolean;
}

function CadCard({
  adId,
  header,
  oldPriceView,
  keyInfo,
  age,
  county,
  countyTown,
  sectionDisplayName,
  sectionName,
  sellerType,
  financeSummary,
  photos,
  userSaved = false,
  dealer,
  cardPayments,
  deliveryAvailable,
  manufacturerApprovedBadge,
  specialOffer,
  meritsV2,
  wanted,
  greenlightVerified,
  herdwatchVerified,
  cdnUrl,
  id,
  hideSave,
  imageAlt,
  highlight,
  highlightText,
  isGrid = false,
  isMobile = false,
  priceOnRequest = false,
  renderAdditionalHeaderInfo,
  renderSubtitle,
  shouldTruncateHeader = false,
  ...rest
}: CadCardProps) {
  const { user } = useUserContext();
  const { handleLogin, accessToken } = useAuthOptions();
  const authorisationHeader = formatAuthorizationHeader(accessToken);

  const { onSave, isAdSaved } = useSaveAd({
    adId: id,
    userSaved: userSaved,
    section: sectionName ?? '',
    sellerType,
    sectionName,
    userId: user?.id,
    authorisationHeader,
    handleLogin,
  });

  const infoItems = [];

  if (financeSummary?.price) {
    infoItems.push({
      title: 'From',
      content: `€${financeSummary.price}`,
      subText: 'p/m',
    });
  }

  const photo = photos && photos[0].small2;

  let injectedKeyInfo;

  if (isGrid) {
    injectedKeyInfo = [];
    if (age) injectedKeyInfo.push(age);
    if (county) {
      injectedKeyInfo.push(county);
    }
    if (sectionDisplayName && !isMobile)
      injectedKeyInfo.push(sectionDisplayName);
  } else {
    injectedKeyInfo = [...(keyInfo || [])];
    if (age) injectedKeyInfo.push(age);
    if (county) {
      if (countyTown) {
        injectedKeyInfo.push(`${countyTown}, ${county}`);
      } else {
        injectedKeyInfo.push(county);
      }
    }
    if (sectionDisplayName) injectedKeyInfo.push(sectionDisplayName);
  }

  return (
    <Card
      title={header}
      imgSrc={photo}
      imgSrcWebp={(photos && photos[0].small2Webp) || undefined}
      noImgSrc={`${cdnUrl}/images/fallbacks/card-no-image.svg`}
      oldPrice={(oldPriceView?.showOldPrice && oldPriceView.oldPrice) || ''}
      keyInfoList={injectedKeyInfo}
      info={infoItems}
      onSave={hideSave ? undefined : onSave}
      userSaved={isAdSaved}
      highlight={highlight}
      highlightText={highlightText}
      header={{
        title: dealer?.name,
        subTitle: renderSubtitle ? (
          <>{renderSubtitle}</>
        ) : (
          dealer?.franchiseType
        ),
        logo: dealer?.logo?.small,
      }}
      tags={
        !isGrid && (
          <TrustIndicatorTags
            key="tags"
            deliveryAvailable={deliveryAvailable ?? false}
            cardPayments={cardPayments}
            meritsV2={meritsV2}
            specialOffer={specialOffer}
            wanted={wanted}
            greenlightVerified={greenlightVerified}
            manufacturerApprovedBadge={manufacturerApprovedBadge}
          />
        )
      }
      imgAlt={imageAlt}
      priceOnRequest={priceOnRequest}
      renderAdditionalHeaderInfo={renderAdditionalHeaderInfo}
      {...rest}
    />
  );
}

export { CadCard };
